import { Button } from "@/components/ui/button";
import { useTransmitChannel } from "@/hooks/use-transmit-channel";
import {
  requestStartCallRingtone,
  requestStopCallRingtone,
} from "@/lib/audio-notification-coordinator";
import { normalizeNotificationSoundSettings } from "@/lib/notification-sounds";
import { openVideoCallOnce } from "@/lib/video-call-window-coordinator";
import type { SharedData } from "@/types";
import { usePage } from "@inertiajs/react";
import axios from "axios";
import { PhoneOff, Video, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { urlFor } from "~/client";

type IncomingVideoCall = {
  id: number;
  providerType: string;
  providerName: string;
  url: string;
  roomName: string | null;
  status: string;
  startedByCompanyUserId: number | null;
  startedByName: string | null;
};

type VideoCallEvent = {
  type?: string;
  videoCall?: Omit<IncomingVideoCall, "startedByCompanyUserId" | "startedByName">;
  conversationId?: number;
  startedByCompanyUserId?: number | null;
  startedByName?: string | null;
};

type VideoCallOpenedEvent = CustomEvent<{
  videoCallId?: number;
}>;

type WorkspaceTeam = {
  conversationId?: number | null;
};

type WorkspaceChannel = {
  conversationId?: number | null;
};

type WorkspaceDirectMessage = {
  conversationId?: number | null;
};

type GlobalVideoCallPageProps = SharedData & {
  workspaceTeams?: WorkspaceTeam[];
  workspaceChannels?: WorkspaceChannel[];
  workspaceDirectMessages?: WorkspaceDirectMessage[];
};

type AuthWithCompanyUserId = SharedData["auth"] & {
  company_user_id?: number | string | null;
};

type VideoCallChannelSubscriberProps = {
  conversationId: number;
  onEvent: (event: VideoCallEvent) => void;
};

function sameId(
  left: number | string | bigint | null | undefined,
  right: number | string | bigint,
) {
  return Number(left) === Number(right);
}

function VideoCallChannelSubscriber({ conversationId, onEvent }: VideoCallChannelSubscriberProps) {
  useTransmitChannel<VideoCallEvent>(
    `${window.location.origin}/internal/conversations/${conversationId}/video-calls`,
    onEvent,
  );

  return null;
}

export default function GlobalVideoCallListener() {
  const { t } = useTranslation("chat");
  const { props } = usePage<GlobalVideoCallPageProps>();
  const auth = props.auth as AuthWithCompanyUserId;
  const currentCompanyUserId = auth.company_user_id ?? null;
  const [incomingVideoCall, setIncomingVideoCall] = useState<IncomingVideoCall | null>(null);
  const [outgoingVideoCall, setOutgoingVideoCall] = useState<IncomingVideoCall | null>(null);
  const outgoingVideoCallUrlsRef = useRef<Map<number, string>>(new Map());
  const openedAcceptedVideoCallIdsRef = useRef<Set<number>>(new Set());
  const outgoingRingtoneRef = useRef<{
    videoCallId: number;
    stop: () => void;
  } | null>(null);

  const conversationIds = useMemo(() => {
    const ids = new Set<number>();
    for (const item of [
      ...(props.workspaceTeams ?? []),
      ...(props.workspaceChannels ?? []),
      ...(props.workspaceDirectMessages ?? []),
    ]) {
      if (item.conversationId) {
        ids.add(Number(item.conversationId));
      }
    }

    return [...ids];
  }, [props.workspaceChannels, props.workspaceDirectMessages, props.workspaceTeams]);

  useEffect(() => {
    if (!incomingVideoCall) {
      return;
    }

    const soundSettings = normalizeNotificationSoundSettings(auth?.notificationSoundSettings);
    if (!soundSettings.enabled) {
      return;
    }

    requestStartCallRingtone({ id: incomingVideoCall.id, volume: soundSettings.volume });

    return () => requestStopCallRingtone(incomingVideoCall.id);
  }, [auth?.notificationSoundSettings, incomingVideoCall]);

  const stopOutgoingRingtone = useCallback((videoCallId: number) => {
    if (
      !outgoingRingtoneRef.current ||
      !sameId(outgoingRingtoneRef.current.videoCallId, videoCallId)
    ) {
      return;
    }

    outgoingRingtoneRef.current.stop();
    outgoingRingtoneRef.current = null;
  }, []);

  const startOutgoingRingtone = useCallback(
    (videoCallId: number) => {
      if (outgoingRingtoneRef.current) {
        outgoingRingtoneRef.current.stop();
        outgoingRingtoneRef.current = null;
      }

      const soundSettings = normalizeNotificationSoundSettings(auth?.notificationSoundSettings);
      if (!soundSettings.enabled) {
        return;
      }

      requestStartCallRingtone({ id: videoCallId, volume: soundSettings.volume });
      outgoingRingtoneRef.current = {
        videoCallId,
        stop: () => requestStopCallRingtone(videoCallId),
      };
    },
    [auth?.notificationSoundSettings],
  );

  useEffect(() => {
    return () => {
      outgoingRingtoneRef.current?.stop();
      outgoingRingtoneRef.current = null;
    };
  }, []);

  const updateVideoCallStatus = useCallback((videoCallId: number, status: "joined" | "ended") => {
    void axios
      .patch(urlFor("chat.video-calls.status.update", { videoCallId }), { status })
      .catch((error) => console.error("Failed to update video call status:", error));
  }, []);

  useEffect(() => {
    const handleVideoCallOpened = (event: Event) => {
      const videoCallId = Number((event as VideoCallOpenedEvent).detail?.videoCallId);
      if (!videoCallId) {
        return;
      }

      updateVideoCallStatus(videoCallId, "joined");
      stopOutgoingRingtone(videoCallId);
      setIncomingVideoCall((current) => (sameId(current?.id, videoCallId) ? null : current));
    };

    window.addEventListener("airie:video-call-opened", handleVideoCallOpened);

    return () => {
      window.removeEventListener("airie:video-call-opened", handleVideoCallOpened);
    };
  }, [stopOutgoingRingtone, updateVideoCallStatus]);

  const handleVideoCallEvent = useCallback(
    (event: VideoCallEvent) => {
      if (event.type === "video_call.updated" && event.videoCall) {
        const updatedVideoCallId = event.videoCall.id;

        if (event.videoCall.status === "joined" || event.videoCall.status === "ended") {
          setIncomingVideoCall((current) =>
            sameId(current?.id, updatedVideoCallId) ? null : current,
          );
          setOutgoingVideoCall((current) =>
            sameId(current?.id, updatedVideoCallId) ? null : current,
          );
          stopOutgoingRingtone(Number(updatedVideoCallId));
        }

        const outgoingUrl = outgoingVideoCallUrlsRef.current.get(Number(updatedVideoCallId));
        if (
          event.videoCall.status === "joined" &&
          outgoingUrl &&
          !openedAcceptedVideoCallIdsRef.current.has(Number(updatedVideoCallId))
        ) {
          openedAcceptedVideoCallIdsRef.current.add(Number(updatedVideoCallId));
          openVideoCallOnce(updatedVideoCallId, outgoingUrl);
        }
        return;
      }

      if (event.type !== "video_call.started" || !event.videoCall) {
        return;
      }

      if (sameId(event.startedByCompanyUserId, currentCompanyUserId ?? 0)) {
        outgoingVideoCallUrlsRef.current.set(Number(event.videoCall.id), event.videoCall.url);
        setOutgoingVideoCall({
          ...event.videoCall,
          startedByCompanyUserId: event.startedByCompanyUserId ?? null,
          startedByName: event.startedByName ?? null,
        });
        startOutgoingRingtone(Number(event.videoCall.id));
        return;
      }

      setIncomingVideoCall({
        ...event.videoCall,
        startedByCompanyUserId: event.startedByCompanyUserId ?? null,
        startedByName: event.startedByName ?? null,
      });
    },
    [currentCompanyUserId, startOutgoingRingtone, stopOutgoingRingtone],
  );

  const acceptIncomingVideoCall = () => {
    if (!incomingVideoCall) {
      return;
    }

    updateVideoCallStatus(incomingVideoCall.id, "joined");
    openVideoCallOnce(incomingVideoCall.id, incomingVideoCall.url);
    setIncomingVideoCall(null);
  };

  const rejectIncomingVideoCall = () => {
    if (incomingVideoCall) {
      updateVideoCallStatus(incomingVideoCall.id, "ended");
    }
    setIncomingVideoCall(null);
  };

  const cancelOutgoingVideoCall = () => {
    if (!outgoingVideoCall) {
      return;
    }

    updateVideoCallStatus(outgoingVideoCall.id, "ended");
    stopOutgoingRingtone(outgoingVideoCall.id);
    setOutgoingVideoCall(null);
  };

  return (
    <>
      {conversationIds.map((conversationId) => (
        <VideoCallChannelSubscriber
          key={conversationId}
          conversationId={conversationId}
          onEvent={handleVideoCallEvent}
        />
      ))}

      {outgoingVideoCall && (
        <div className="fixed right-4 top-4 z-50 w-[min(360px,calc(100%-2rem))] rounded-lg border bg-background p-3 shadow-lg">
          <div className="flex items-start gap-3">
            <div className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
              <Video className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold">{t("videoCalls.outgoingTitle")}</p>
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    {t("videoCalls.outgoingDescription", {
                      provider: outgoingVideoCall.providerName,
                    })}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="-mr-2 -mt-2 h-8 w-8"
                  onClick={cancelOutgoingVideoCall}
                  aria-label={t("videoCalls.cancel")}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <Button type="button" variant="outline" size="sm" onClick={cancelOutgoingVideoCall}>
                  <PhoneOff className="mr-2 h-4 w-4" />
                  {t("videoCalls.cancel")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {incomingVideoCall && (
        <div className="fixed right-4 top-4 z-50 w-[min(360px,calc(100%-2rem))] rounded-lg border bg-background p-3 shadow-lg">
          <div className="flex items-start gap-3">
            <div className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
              <Video className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold">{t("videoCalls.incomingTitle")}</p>
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    {t("videoCalls.incomingDescription", {
                      provider: incomingVideoCall.providerName,
                      caller: incomingVideoCall.startedByName ?? t("videoCalls.unknownCaller"),
                    })}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="-mr-2 -mt-2 h-8 w-8"
                  onClick={() => setIncomingVideoCall(null)}
                  aria-label={t("actions.cancel")}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <Button type="button" variant="outline" size="sm" onClick={rejectIncomingVideoCall}>
                  <PhoneOff className="mr-2 h-4 w-4" />
                  {t("videoCalls.reject")}
                </Button>
                <Button type="button" size="sm" onClick={acceptIncomingVideoCall}>
                  <Video className="mr-2 h-4 w-4" />
                  {t("videoCalls.accept")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
